/**
 * Custom mirror calculator — integrated PDP (custom rug accordion pattern).
 * Reads window.MirrorData, updates .product-metas__price, toggles main add-to-cart.
 */
(function () {
  'use strict';

  var ROUND_HANDLE = 'round';
  var DIM_STEP = 0.5;
  var SEL = {
    root: '[data-mirror-calculator]',
    injectShape: '#mirror-pdp-inject-shape',
    injectColor: '#mirror-pdp-inject-color',
    injectFrame: '#mirror-pdp-inject-frame',
    injectDistress: '#mirror-pdp-inject-distress',
    width: '#mirror-calculator-width',
    height: '#mirror-calculator-height',
    errors: '#mirror-calculator-errors',
    sizeHints: '[data-mirror-size-hints]',
    dimensionErrorWidth: '[data-mirror-dimension-error="width"]',
    dimensionErrorHeight: '[data-mirror-dimension-error="height"]',
    sizeEnterPrompt: '[data-mirror-size-enter-prompt]',
    priceRoot: '[data-mirror-price-root]',
  };

  var state = {
    shapeHandle: '',
    colorHandle: '',
    frameHandle: '',
    distressHandle: '',
    widthInput: '',
    heightInput: '',
  };

  function toNumber(v) {
    if (v === null || v === undefined || v === '') return NaN;
    var n = Number(v);
    return typeof n === 'number' && !isNaN(n) ? n : NaN;
  }

  function toNumberOr(v, fallback) {
    var n = toNumber(v);
    return isNaN(n) ? fallback : n;
  }

  /** Snap to nearest 0.5" (e.g. 23.03 → 23, 23.3 → 23.5). */
  function snapToHalfInch(n) {
    return Math.round(n * 2) / 2;
  }

  function formatSnappedDimension(n) {
    var snapped = snapToHalfInch(n);
    return snapped % 1 === 0 ? String(snapped) : snapped.toFixed(1);
  }

  /**
   * Normalize typed dimension: snap to 0.5" grid, then clamp to config min/max.
   * @returns {string} empty if invalid input
   */
  function normalizeDimensionInput(raw, min, max) {
    var n = toNumber(raw);
    if (isNaN(n)) return '';
    n = snapToHalfInch(n);
    if (!isNaN(min) && min > 0 && n < min) n = min;
    if (!isNaN(max) && max > 0 && n > max) n = max;
    return formatSnappedDimension(n);
  }

  function getDimensionConfig() {
    var data = getData();
    if (!data || !data.config) return null;
    return normalizeConfig(data.config);
  }

  /**
   * On blur/change: snap manual typing to valid 0.5" value within min/max.
   * @param {HTMLInputElement | null} el
   * @param {'width' | 'height'} which
   */
  function applyDimensionSnap(el, which) {
    if (!el || String(el.value).trim() === '') return;
    var config = getDimensionConfig();
    if (!config) return;

    var min = which === 'width' ? config.min_width : config.min_height;
    var max = which === 'width' ? config.max_width : config.max_height;
    var normalized = normalizeDimensionInput(el.value, min, max);
    if (normalized === '') return;

    el.value = normalized;
    if (which === 'width') {
      state.widthInput = normalized;
    } else {
      state.heightInput = normalized;
    }
  }

  function findByHandle(list, handle) {
    if (!list || !handle) return null;
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].handle === handle) return list[i];
    }
    return null;
  }

  /** @param {Array<{handle: string}>|undefined} list */
  function filterHeavyDistresses(list) {
    if (!list || !list.length) return [];
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (!item || !item.handle) continue;
      if (String(item.handle).toLowerCase().indexOf('medium') !== -1) continue;
      out.push(item);
    }
    return out;
  }

  /** Prefer explicit heavy-distress handle when auto-selecting. */
  function resolveHeavyDistressHandle(distresses) {
    var list = filterHeavyDistresses(distresses);
    if (!list.length) return '';
    for (var i = 0; i < list.length; i++) {
      var h = String(list[i].handle).toLowerCase();
      if (h.indexOf('heavy') !== -1) return list[i].handle;
    }
    return list[0].handle;
  }

  function getData() {
    return typeof window !== 'undefined' && window.MirrorData ? window.MirrorData : null;
  }

  function normalizeConfig(raw) {
    if (!raw || typeof raw !== 'object') return null;
    return {
      flat_addon: toNumberOr(raw.flat_addon, 0),
      premium_color_surcharge: toNumberOr(raw.premium_color_surcharge, 0),
      mid_color_surcharge: toNumberOr(raw.mid_color_surcharge, 5),
      premium_frame_surcharge: toNumberOr(raw.premium_frame_surcharge, 0),
      min_height: toNumberOr(raw.min_height, 0),
      max_height: toNumberOr(raw.max_height, 0),
      min_width: toNumberOr(raw.min_width, 0),
      max_width: toNumberOr(raw.max_width, 0),
    };
  }

  /**
   * Theme money: cents → string (Shopify.formatMoney + theme.moneyFormat when available).
   * @param {number} cents
   */
  function formatMoneyCents(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }
    var dollars = (Number(cents) / 100).toFixed(2);
    var parts = dollars.split('.');
    var intPart = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    return '$' + intPart + '.' + parts[1];
  }

  /** UI display: round to nearest $5 and format as whole dollars. @param {number} dollars */
  function formatRetailDollars(dollars) {
    var rounded = Math.round(Number(dollars) / 5) * 5;
    var cents = Math.round(rounded) * 100;
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var wholeFormat = format.replace(/\{\{\s*amount\s*\}\}/g, '{{amount_no_decimals}}');
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, wholeFormat);
    }
    var intPart = String(Math.round(rounded)).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    return '$' + intPart;
  }

  function getPdpPriceLeaf() {
    var root = document.querySelector(SEL.priceRoot);
    if (!root) return null;
    return (
      root.querySelector('.price-value__current') ||
      root.querySelector('.price-value__lowest') ||
      root.querySelector('.price-value')
    );
  }

  function getAddToCartButton() {
    var root = document.querySelector(SEL.root);
    var form = root && root.closest('form[action*="/cart/add"]');
    if (form) {
      var btn =
        form.querySelector('button[type="submit"][name="add"]') ||
        form.querySelector('button.product-form__cart-submit');
      if (btn) return btn;
    }
    return (
      document.querySelector('form[action*="/cart/add"] button[type="submit"][name="add"]') ||
      document.querySelector('button.product-form__cart-submit[add-to-cart]')
    );
  }

  /**
   * Resolve dimensions for validation, pricing, and cart (snap to 0.5", clamp to min/max).
   * @returns {{ width: number, height: number, widthStr: string, heightStr: string }}
   */
  function getResolvedDimensions(config, isRound) {
    var widthStr = normalizeDimensionInput(state.widthInput, config.min_width, config.max_width);
    var heightStr = isRound
      ? widthStr
      : normalizeDimensionInput(state.heightInput, config.min_height, config.max_height);
    return {
      width: toNumber(widthStr),
      height: toNumber(heightStr),
      widthStr: widthStr,
      heightStr: heightStr,
    };
  }

  function validateDimensions(config, isRound, width, height) {
    var errors = [];
    if (isRound) {
      if (isNaN(width)) errors.push('Enter a diameter (inches).');
      else if (width < config.min_width || width > config.max_width) {
        errors.push('Diameter must be between ' + config.min_width + '" and ' + config.max_width + '".');
      }
    } else {
      if (isNaN(width)) errors.push('Enter a width (inches).');
      else if (width < config.min_width || width > config.max_width) {
        errors.push('Width must be between ' + config.min_width + '" and ' + config.max_width + '".');
      }
      if (isNaN(height)) errors.push('Enter a height (inches).');
      else if (height < config.min_height || height > config.max_height) {
        errors.push('Height must be between ' + config.min_height + '" and ' + config.max_height + '".');
      }
    }
    return errors;
  }

  function runValidation() {
    var data = getData();
    var errors = [];
    if (!data) {
      errors.push('Mirror configuration could not be loaded. Please refresh the page.');
      return { valid: false, errors: errors, dimensionErrors: [], config: null };
    }
    var config = normalizeConfig(data.config);
    if (!config) {
      errors.push('Pricing configuration is missing. Please contact support.');
      return { valid: false, errors: errors, dimensionErrors: [], config: null };
    }
    if (!state.shapeHandle) errors.push('Select a shape.');
    if (!state.colorHandle) errors.push('Select a color.');
    if (!state.frameHandle) errors.push('Select a frame.');
    if (!state.distressHandle) errors.push('Select a distress level.');

    var isRound = state.shapeHandle === ROUND_HANDLE;
    var resolved = getResolvedDimensions(config, isRound);
    var dimensionErrors = validateDimensions(config, isRound, resolved.width, resolved.height);
    var allErrors = errors.concat(dimensionErrors);
    return {
      valid: allErrors.length === 0,
      errors: errors,
      dimensionErrors: dimensionErrors,
      config: config,
      resolved: resolved,
    };
  }

  function computePricing(config, resolved) {
    var data = getData();
    if (!data || !config) return null;
    var shape = findByHandle(data.shapes, state.shapeHandle);
    var color = findByHandle(data.colors, state.colorHandle);
    var frame = findByHandle(data.frames, state.frameHandle);
    var distress = findByHandle(data.distresses, state.distressHandle);
    if (!shape || !color || !frame || !distress) return null;

    if (!resolved) {
      var isRound = state.shapeHandle === ROUND_HANDLE;
      resolved = getResolvedDimensions(config, isRound);
    }
    var width = resolved.width;
    var height = resolved.height;
    if (isNaN(width) || isNaN(height)) return null;

    // No intermediate rounding: sqft, cost, retail stay full float until final step (matches cart transform).
    var sqft = (height * width) / 144;
    var colorTier = Number(color.tier);
    var colorPremium =
      colorTier === 2
        ? toNumberOr(config.premium_color_surcharge, 0)
        : colorTier === 1.5
          ? toNumberOr(config.mid_color_surcharge, 0)
          : 0;
    var framePremium = Number(frame.tier) === 2 ? toNumberOr(config.premium_frame_surcharge, 0) : 0;
    var rate = toNumberOr(shape.base_rate, 0) + colorPremium + framePremium;
    var cost =
      sqft * rate + toNumberOr(config.flat_addon, 0) + toNumberOr(distress.surcharge, 0);
    var retail = cost * 3;
    return { sqft: sqft, rate: rate, cost: cost, retail: retail };
  }

  /**
   * Sanitize handle for use in HTML id.
   * @param {string} h
   */
  function sanitizeIdPart(h) {
    return String(h || '')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * @param {unknown} url
   * @returns {boolean}
   */
  function hasUsableImageUrl(url) {
    if (url === null || url === undefined) return false;
    var s = String(url).trim();
    return s.length > 0 && s !== 'null' && s !== 'undefined';
  }

  /** Upsize swatch/thumbnail URLs for main gallery display. @param {string} url */
  function toGalleryImageUrl(url) {
    return String(url).replace(/width=\d+/, 'width=1200');
  }

  /** Smaller URL for thumbnail strip. @param {string} url */
  function toThumbImageUrl(url) {
    return String(url).replace(/width=\d+/, 'width=200');
  }

  /** @param {string} colorHandle */
  function getColorImageUrl(colorHandle) {
    var data = getData();
    var color = data && findByHandle(data.colors, colorHandle);
    if (color && hasUsableImageUrl(color.image)) return String(color.image).trim();
    return null;
  }

  /**
   * @param {Element|null} slide
   * @param {string} src
   * @returns {boolean}
   */
  function setSlideImageSources(slide, src) {
    if (!slide) return false;
    var imgs = slide.querySelectorAll('img.inlineImg, img');
    if (!imgs.length) return false;

    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      img.onerror = null;
      img.src = src;
      img.srcset = '';
      if (img.dataset) img.dataset.src = src;

      var pictureEl = img.closest('picture');
      if (pictureEl) {
        var pictureSources = pictureEl.querySelectorAll('source');
        for (var k = 0; k < pictureSources.length; k++) {
          pictureSources[k].srcset = '';
        }
      }
    }
    return true;
  }

  /**
   * Combo gallery image for shape + color + frame (Heavy Distress is constant).
   * @param {string} shapeHandle
   * @param {string} colorHandle
   * @param {string} frameHandle
   * @returns {string|null}
   */
  function getMirrorImage(shapeHandle, colorHandle, frameHandle) {
    if (!shapeHandle || !colorHandle || !frameHandle) return null;
    var data = getData();
    if (!data) return null;
    var overrideMap =
      data.comboImageOverrides && data.comboImageOverrides.byKey
        ? data.comboImageOverrides.byKey
        : data.comboImageOverrides;
    var comboMap = data.comboImages && data.comboImages.byKey ? data.comboImages.byKey : null;
    var shapeCandidates = [shapeHandle];
    if (shapeHandle === 'rectangular') shapeCandidates.push('rectangle');
    if (shapeHandle === 'rectangle') shapeCandidates.push('rectangular');

    for (var i = 0; i < shapeCandidates.length; i++) {
      var key = shapeCandidates[i] + '|' + colorHandle + '|' + frameHandle;
      if (overrideMap && hasUsableImageUrl(overrideMap[key])) {
        return String(overrideMap[key]).trim();
      }
      if (comboMap && hasUsableImageUrl(comboMap[key])) {
        return String(comboMap[key]).trim();
      }
    }
    return null;
  }

  /** @returns {string} shape|color|frame */
  function getMirrorComboKey() {
    return state.shapeHandle + '|' + state.colorHandle + '|' + state.frameHandle;
  }

  /** Expected Shopify Files filename for current combo (if mapped). */
  function getExpectedMirrorFilename() {
    var data = getData();
    if (!data || !data.comboFilenames || !data.comboFilenames.byKey) return null;
    var filenameMap = data.comboFilenames.byKey;
    var shapeCandidates = [state.shapeHandle];
    if (state.shapeHandle === 'rectangular') shapeCandidates.push('rectangle');
    if (state.shapeHandle === 'rectangle') shapeCandidates.push('rectangular');

    for (var i = 0; i < shapeCandidates.length; i++) {
      var key = shapeCandidates[i] + '|' + state.colorHandle + '|' + state.frameHandle;
      var name = filenameMap[key];
      if (name) return String(name);
    }
    return null;
  }

  /**
   * Resolve gallery image for current shape+color+frame selection.
   * @returns {{
   *   comboKey: string,
   *   expectedFilename: string|null,
   *   source: 'combo-image'|'none',
   *   comboUrl: string|null,
   *   showingUrl: string|null
   * }}
   */
  function resolveGalleryImage() {
    var comboKey = getMirrorComboKey();
    var expectedFilename = getExpectedMirrorFilename();
    var comboUrl = getMirrorImage(state.shapeHandle, state.colorHandle, state.frameHandle);
    /** @type {'combo-image'|'none'} */
    var source = comboUrl ? 'combo-image' : 'none';
    return {
      comboKey: comboKey,
      expectedFilename: expectedFilename,
      source: source,
      comboUrl: comboUrl || null,
      showingUrl: comboUrl || null,
    };
  }

  /**
   * Client/debug: log which file should show in gallery and what is actually used.
   * @param {{
   *   comboKey: string,
   *   expectedFilename: string|null,
   *   source: 'combo-image'|'none',
   *   comboUrl: string|null,
   *   showingUrl: string|null
   * }} resolution
   */
  function logGalleryImageDebug(resolution) {
    console.log('[MirrorCalculator] Gallery image debug', {
      selection: {
        shape: state.shapeHandle,
        color: state.colorHandle,
        frame: state.frameHandle,
      },
      comboKey: resolution.comboKey,
      expectedShopifyFile: resolution.expectedFilename || '(not mapped yet)',
      mappedFilename: resolution.expectedFilename,
      imageSource: resolution.source,
      comboImageUrl: resolution.comboUrl,
      showingUrl: resolution.showingUrl,
      note:
        resolution.source === 'none'
          ? 'No combo image found for this key; keeping current gallery image unchanged.'
          : 'Showing combo gallery image.',
    });
  }

  var initialGalleryApplyTimer = null;
  var initialGalleryApplyAttempts = 0;
  var INITIAL_GALLERY_APPLY_MAX = 24;
  var INITIAL_GALLERY_APPLY_INTERVAL_MS = 150;

  /**
   * @param {{ silent?: boolean }} [opts]
   * @returns {boolean} true when combo image was applied to the main gallery slide
   */
  function updateProductGalleryForSelection(opts) {
    var silent = !!(opts && opts.silent);
    var resolution = resolveGalleryImage();
    if (!silent) logGalleryImageDebug(resolution);
    if (!resolution.showingUrl) {
      if (!silent) {
        console.warn('[MirrorCalculator] No combo image for current selection; gallery left unchanged.', {
          comboKey: resolution.comboKey,
          expectedShopifyFile: resolution.expectedFilename || '(not mapped yet)',
        });
      }
      return false;
    }

    var gallerySrc = toGalleryImageUrl(resolution.showingUrl);
    var thumbSrc = toThumbImageUrl(resolution.showingUrl);

    var gallery = document.querySelector('[product-gallery]');
    if (!gallery) return false;

    var mainSlide =
      gallery.querySelector('.product-gallery__main .swiper--main .swiper-slide[data-selected]') ||
      gallery.querySelector('.product-gallery__main .swiper--main .swiper-slide-active') ||
      gallery.querySelector('.product-gallery__main .swiper--main .swiper-slide');
    if (!mainSlide) return false;

    var mainApplied = setSlideImageSources(mainSlide, gallerySrc);
    if (!mainApplied) return false;

    var thumbSlide =
      gallery.querySelector('.product-gallery__thumbs .swiper--thumbs .swiper-slide-thumb-active') ||
      gallery.querySelector('.product-gallery__thumbs .swiper--thumbs .swiper-slide-active') ||
      gallery.querySelector('.product-gallery__thumbs .swiper--thumbs .swiper-slide');
    setSlideImageSources(thumbSlide, thumbSrc);

    var pgInstance = gallery.productGalleryInstance;
    if (pgInstance && pgInstance.instance) {
      pgInstance.instance.slideTo(0, 0, false);
      pgInstance.instance.update();
      if (pgInstance.instance.thumbs && pgInstance.instance.thumbs.swiper) {
        pgInstance.instance.thumbs.swiper.slideTo(0, 0, false);
        pgInstance.instance.thumbs.swiper.update();
      }
    }

    return true;
  }

  function scheduleInitialMirrorGalleryImage() {
    if (initialGalleryApplyTimer) {
      clearTimeout(initialGalleryApplyTimer);
      initialGalleryApplyTimer = null;
    }
    initialGalleryApplyAttempts = 0;

    function attempt() {
      initialGalleryApplyAttempts += 1;
      var isLastAttempt = initialGalleryApplyAttempts >= INITIAL_GALLERY_APPLY_MAX;
      var applied = updateProductGalleryForSelection({ silent: !isLastAttempt });
      if (applied) return;
      if (isLastAttempt) return;
      initialGalleryApplyTimer = setTimeout(attempt, INITIAL_GALLERY_APPLY_INTERVAL_MS);
    }

    attempt();
  }

  function bindMirrorGalleryLifecycle() {
    if (window.__mirrorGalleryLifecycleBound) return;
    window.__mirrorGalleryLifecycleBound = true;

    function onGalleryLifecycleEvent() {
      if (!document.querySelector(SEL.root)) return;
      scheduleInitialMirrorGalleryImage();
    }

    document.addEventListener('variant:change', onGalleryLifecycleEvent);
    document.addEventListener('variant:changed', onGalleryLifecycleEvent);

    var gallery = document.querySelector('[product-gallery]');
    if (gallery && typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function () {
        if (!gallery.classList.contains('product-gallery--idle')) {
          onGalleryLifecycleEvent();
        }
      });
      observer.observe(gallery, { attributes: true, attributeFilter: ['class'] });
    }
  }

  /**
   * Custom Rugs–style swatch card (image + label). Matches custom-rug-option markup.
   * @param {DocumentFragment} fragment
   * @param {object} opts
   */
  function appendMirrorSwatchOption(fragment, opts) {
    var item = opts.item;
    var name = opts.name;
    var rugModifier = opts.rugModifier;
    var radioName = opts.radioName;
    var id = opts.id;
    var currentHandle = opts.currentHandle;
    var showTierPremium = opts.showTierPremium;

    var label = document.createElement('label');
    label.className = 'custom-rug-option mirror-option-swatch custom-rug-option__' + rugModifier;
    label.setAttribute('for', id);
    label.setAttribute('tabindex', '0');

    if (showTierPremium && Number(item.tier) === 2) {
      label.classList.add('mirror-option-swatch--tier-2');
    }

    if (rugModifier === 'shape' && String(item.handle).toLowerCase() === ROUND_HANDLE) {
      label.classList.add('mirror-option-swatch--round');
    }

    var input = document.createElement('input');
    input.type = 'radio';
    input.name = radioName;
    input.id = id;
    input.value = item.handle;
    input.hidden = true;
    input.required = true;
    input.checked = item.handle === currentHandle;

    var imageHolder = document.createElement('span');
    imageHolder.className = 'custom-rug-option__imageHolder custom-rug-option__border';

    var img = document.createElement('img');
    img.className = 'custom-rug-option__image';
    img.src = String(item.image).trim();
    img.alt = item.name || item.handle || name;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = 120;
    img.height = 120;

    img.addEventListener('error', function onImgError() {
      img.removeEventListener('error', onImgError);
      img.remove();
      imageHolder.classList.add('mirror-option-swatch__imageHolder--broken');
      var fallback = document.createElement('span');
      fallback.className = 'mirror-option-swatch__fallback-label';
      fallback.textContent = item.name || item.handle || '';
      imageHolder.appendChild(fallback);
    });

    imageHolder.appendChild(img);

    var caption = document.createElement('p');
    caption.className = 'custom-rug-option__label';
    caption.textContent = item.name || item.handle;

    label.appendChild(input);
    label.appendChild(imageHolder);
    label.appendChild(caption);
    fragment.appendChild(label);
  }

  /**
   * Text-only fallback when metaobject image is missing.
   */
  function appendMirrorTextOption(fragment, opts) {
    var item = opts.item;
    var name = opts.name;
    var radioName = opts.radioName;
    var id = opts.id;
    var currentHandle = opts.currentHandle;
    var showTierPremium = opts.showTierPremium;

    var label = document.createElement('label');
    label.className = 'mirror-option-chip mirror-option-chip--text-only';
    label.setAttribute('for', id);

    if (showTierPremium && Number(item.tier) === 2) {
      label.classList.add('mirror-option-chip--tier-2');
    }

    var input = document.createElement('input');
    input.type = 'radio';
    input.name = radioName;
    input.id = id;
    input.value = item.handle;
    input.className = 'mirror-option-chip__input';
    input.checked = item.handle === currentHandle;

    var span = document.createElement('span');
    span.className = 'mirror-option-chip__text custom-rug-option__label';
    span.textContent = item.name || item.handle;

    label.appendChild(input);
    label.appendChild(span);
    fragment.appendChild(label);
  }

  /**
   * Mirror option UI — Custom Rugs swatch pattern with text fallback.
   * Renders options as direct children of container (display:contents wrapper) for theme grid.
   *
   * @param {HTMLElement | null} container
   * @param {string} name - shape | color | frame | distress
   * @param {string} labelText - aria-label for group
   * @param {Array<{handle: string, name?: string, tier?: number, image?: string|null}>} items
   * @param {string} currentHandle
   * @param {function(string): void} onChange
   * @param {{ rugModifier: string, showTierPremium?: boolean }} config
   */
  function renderMirrorSwatchGroup(container, name, labelText, items, currentHandle, onChange, config) {
    if (!container) return;
    container.innerHTML = '';
    container.setAttribute('role', 'radiogroup');
    container.setAttribute('aria-label', labelText);

    if (!items || items.length === 0) {
      container.textContent = 'No options available.';
      return;
    }

    var rugModifier = (config && config.rugModifier) || 'color';
    var showTierPremium = !!(config && config.showTierPremium);
    var radioName = 'mirror-option-' + name;
    var usedIds = {};
    var fragment = document.createDocumentFragment();

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || !item.handle) continue;

      var safe = sanitizeIdPart(item.handle);
      var id = 'mirror-' + name + '-' + safe;
      if (usedIds[id]) id = id + '-' + i;
      usedIds[id] = true;

      var baseOpts = {
        item: item,
        name: name,
        rugModifier: rugModifier,
        radioName: radioName,
        id: id,
        currentHandle: currentHandle,
        showTierPremium: showTierPremium,
      };

      if (hasUsableImageUrl(item.image)) {
        appendMirrorSwatchOption(fragment, baseOpts);
      } else {
        appendMirrorTextOption(fragment, baseOpts);
      }
    }

    container.appendChild(fragment);

    container.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.type === 'radio' && t.name === radioName) {
        onChange(t.value);
      }
    });
  }

  function renderErrors(errors) {
    var el = document.querySelector(SEL.errors);
    if (!el) return;
    el.innerHTML = '';
    if (!errors || errors.length === 0) return;
    var ul = document.createElement('ul');
    ul.className = 'mirror-calculator__error-list';
    for (var i = 0; i < errors.length; i++) {
      var li = document.createElement('li');
      li.textContent = errors[i];
      ul.appendChild(li);
    }
    el.appendChild(ul);
  }

  /** Permanent helper copy inside the Size step (not blocking errors). */
  function renderSizeHints(config, isRound) {
    var el = document.querySelector(SEL.sizeHints);
    if (!el || !config) return;

    var lines = [];
    var incrementNote = 'Must be in 0.5" increments (e.g. 24, 24.5).';

    if (isRound) {
      lines.push('Diameter ' + incrementNote);
      lines.push(
        'Diameter must be between ' + config.min_width + '" and ' + config.max_width + '".'
      );
    } else {
      lines.push('Width and Height Must be in 0.5" increments (e.g. 24, 24.5)');
      lines.push('Width must be between ' + config.min_width + '" and ' + config.max_width + '".');
      lines.push(
        'Height must be between ' + config.min_height + '" and ' + config.max_height + '".'
      );
    }

    el.innerHTML = '';
    for (var i = 0; i < lines.length; i++) {
      var line = document.createElement('span');
      line.className = 'mirror-calculator-pdp__size-hint-line';
      line.textContent = lines[i];
      el.appendChild(line);
    }
  }

  function hasSizeBeenEntered(isRound) {
    var w = String(state.widthInput || '').trim();
    if (!w) return false;
    if (isRound) return true;
    return !!String(state.heightInput || '').trim();
  }

  /** Pink italic prompt above Add to cart — hidden once size fields are filled. */
  function renderSizeEnterPrompt(isRound) {
    var el = document.querySelector(SEL.sizeEnterPrompt);
    if (!el) return;
    el.hidden = hasSizeBeenEntered(isRound);
  }

  /** Size-only validation — shown under width/height inputs, never in global error list. */
  function renderSizeDimensionErrors(dimensionErrors, isRound) {
    var widthErrEl = document.querySelector(SEL.dimensionErrorWidth);
    var heightErrEl = document.querySelector(SEL.dimensionErrorHeight);
    var widthInput = document.querySelector(SEL.width);
    var heightInput = document.querySelector(SEL.height);
    var widthMsgs = [];
    var heightMsgs = [];

    if (dimensionErrors && dimensionErrors.length) {
      for (var i = 0; i < dimensionErrors.length; i++) {
        var msg = dimensionErrors[i];
        if (!msg) continue;
        if (
          msg.indexOf('width') === 0 ||
          msg.indexOf('Width') === 0 ||
          msg.indexOf('diameter') === 0 ||
          msg.indexOf('Diameter') === 0
        ) {
          widthMsgs.push(msg);
        } else if (msg.indexOf('height') === 0 || msg.indexOf('Height') === 0) {
          heightMsgs.push(msg);
        }
      }
    }

    if (widthErrEl) {
      widthErrEl.textContent = widthMsgs.join(' ');
      widthErrEl.hidden = widthMsgs.length === 0;
    }
    if (heightErrEl) {
      heightErrEl.textContent = heightMsgs.join(' ');
      heightErrEl.hidden = isRound || heightMsgs.length === 0;
    }
    if (widthInput) {
      if (widthMsgs.length) widthInput.setAttribute('aria-invalid', 'true');
      else widthInput.removeAttribute('aria-invalid');
    }
    if (heightInput && !isRound) {
      if (heightMsgs.length) heightInput.setAttribute('aria-invalid', 'true');
      else heightInput.removeAttribute('aria-invalid');
    } else if (heightInput) {
      heightInput.removeAttribute('aria-invalid');
    }
  }

  /** Write snapped dimensions into state, visible inputs, and cart hidden fields. */
  function commitResolvedDimensions(resolved, isRound) {
    if (!resolved) return;
    state.widthInput = resolved.widthStr;
    if (isRound) state.heightInput = resolved.widthStr;
    else state.heightInput = resolved.heightStr;

    var w = document.querySelector(SEL.width);
    var h = document.querySelector(SEL.height);
    if (w && resolved.widthStr) w.value = resolved.widthStr;
    if (h) {
      if (isRound && resolved.widthStr) h.value = resolved.widthStr;
      else if (!isRound && resolved.heightStr) h.value = resolved.heightStr;
    }
  }

  function updateAccordionSelected(selectorAttr, text) {
    var acc = document.querySelector('[cylindo-variant-selector="' + selectorAttr + '"]');
    if (!acc) return;
    var span = acc.querySelector('.cylindoVariant__info--selected');
    if (span) span.textContent = text || '';
  }

  function selectedLabelFromHandle(list, handle) {
    var item = findByHandle(list, handle);
    return item ? item.name || item.handle : '';
  }

  function updateSizeAccordionSummary(resolved) {
    var isRound = state.shapeHandle === ROUND_HANDLE;
    var wDisplay = resolved && resolved.widthStr ? resolved.widthStr : state.widthInput;
    var hDisplay = resolved && resolved.heightStr ? resolved.heightStr : state.heightInput;
    var w = toNumber(wDisplay);
    var h = toNumber(hDisplay);
    var text = '';
    if (isRound) {
      text = !isNaN(w) ? 'Ø ' + wDisplay + '"' : '';
    } else {
      if (!isNaN(w) && !isNaN(h)) text = wDisplay + '" × ' + hDisplay + '"';
      else if (!isNaN(w)) text = wDisplay + '" × …';
      else if (!isNaN(h)) text = '… × ' + hDisplay + '"';
    }
    updateAccordionSelected('Size', text);
  }

  function setHeightRowHidden(hidden) {
    var row = document.querySelector('[data-mirror-dimension="height"]');
    if (!row) return;
    row.classList.toggle('mirror-calculator-pdp__dimension--hidden', hidden);
    row.hidden = hidden;
    row.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    var lbl = row.querySelector('label');
    var inp = row.querySelector('[data-mirror-input="height"]');
    if (lbl && inp) {
      if (hidden) {
        inp.setAttribute('tabindex', '-1');
      } else {
        inp.removeAttribute('tabindex');
      }
    }
  }

  function updateDimensionUI(isRound) {
    var widthInput = document.querySelector(SEL.width);
    if (!widthInput) return;
    var wl = document.querySelector('[data-mirror-dimension="width"] label');
    if (wl) wl.textContent = isRound ? 'Diameter (in)' : 'Width (in)';
    widthInput.removeAttribute('placeholder');
    widthInput.setAttribute('aria-label', isRound ? 'Diameter in inches' : 'Width in inches');
    var heightInput = document.querySelector(SEL.height);
    if (heightInput) heightInput.removeAttribute('placeholder');
    setHeightRowHidden(isRound);
    if (isRound) {
      state.heightInput = state.widthInput;
      var hi = document.querySelector(SEL.height);
      if (hi) hi.value = state.widthInput;
    }
    updateSizeAccordionSummary();
  }

  function syncRoundHeightFromWidth() {
    if (state.shapeHandle !== ROUND_HANDLE) return;
    state.heightInput = state.widthInput;
    var hi = document.querySelector(SEL.height);
    if (hi) hi.value = state.widthInput;
    updateSizeAccordionSummary();
  }

  function renderPdpPrice(valid, retail) {
    var leaf = getPdpPriceLeaf();
    if (!leaf) return;
    if (!valid || retail === null || typeof retail !== 'number' || isNaN(retail)) {
      leaf.textContent = '—';
      return;
    }
    leaf.textContent = formatRetailDollars(retail);
  }

  function setAddToCartEnabled(valid) {
    var btn = getAddToCartButton();
    if (!btn) return;
    if (btn.getAttribute('data-mirror-atc-baseline') === '1') {
      btn.disabled = true;
      return;
    }
    btn.disabled = !valid;
  }

  function refresh() {
    var validation = runValidation();
    var isRound = state.shapeHandle === ROUND_HANDLE;

    if (validation.config) {
      renderSizeHints(validation.config, isRound);
    }

    renderSizeDimensionErrors(validation.dimensionErrors || [], isRound);
    renderSizeEnterPrompt(isRound);
    renderErrors(validation.errors);

    var pricing = null;
    if (validation.valid && validation.config && validation.resolved) {
      pricing = computePricing(validation.config, validation.resolved);
    }

    renderPdpPrice(validation.valid, pricing ? pricing.retail : null);
    setAddToCartEnabled(validation.valid);
    updateSizeAccordionSummary(validation.valid ? validation.resolved : null);
    syncCartPropertyInputs(validation.valid ? getMirrorCartLineProperties() : null);
  }

  function bindDimensionInputs() {
    var w = document.querySelector(SEL.width);
    var h = document.querySelector(SEL.height);

    function onWInput() {
      state.widthInput = w ? w.value : '';
      syncRoundHeightFromWidth();
      refresh();
    }

    function onHInput() {
      state.heightInput = h ? h.value : '';
      refresh();
    }

    function onWSnap() {
      applyDimensionSnap(w, 'width');
      syncRoundHeightFromWidth();
      refresh();
    }

    function onHSnap() {
      applyDimensionSnap(h, 'height');
      refresh();
    }

    if (w) {
      w.addEventListener('input', onWInput);
      w.addEventListener('change', onWSnap);
      w.addEventListener('blur', onWSnap);
    }
    if (h) {
      h.addEventListener('input', onHInput);
      h.addEventListener('change', onHSnap);
      h.addEventListener('blur', onHSnap);
    }
  }

  function initSelectors() {
    var data = getData();
    if (!data) return;

    var heavyDistresses = filterHeavyDistresses(data.distresses);

    if (data.shapes && data.shapes[0] && !state.shapeHandle) state.shapeHandle = data.shapes[0].handle;
    if (data.colors && data.colors[0] && !state.colorHandle) state.colorHandle = data.colors[0].handle;
    if (data.frames && data.frames[0] && !state.frameHandle) state.frameHandle = data.frames[0].handle;
    if (!state.distressHandle) {
      state.distressHandle = resolveHeavyDistressHandle(data.distresses);
    } else {
      var currentDistress = findByHandle(heavyDistresses, state.distressHandle);
      if (!currentDistress) state.distressHandle = resolveHeavyDistressHandle(data.distresses);
    }

    renderMirrorSwatchGroup(
      document.querySelector(SEL.injectShape),
      'shape',
      'Shape',
      data.shapes || [],
      state.shapeHandle,
      function (v) {
        state.shapeHandle = v;
        updateDimensionUI(state.shapeHandle === ROUND_HANDLE);
        syncRoundHeightFromWidth();
        updateAccordionSelected('Shape', selectedLabelFromHandle(data.shapes, v));
        updateProductGalleryForSelection();
        refresh();
      },
      { rugModifier: 'shape', showTierPremium: false }
    );

    renderMirrorSwatchGroup(
      document.querySelector(SEL.injectColor),
      'color',
      'Color',
      data.colors || [],
      state.colorHandle,
      function (v) {
        state.colorHandle = v;
        updateAccordionSelected('Color', selectedLabelFromHandle(data.colors, v));
        updateProductGalleryForSelection();
        refresh();
      },
      { rugModifier: 'color', showTierPremium: true }
    );

    renderMirrorSwatchGroup(
      document.querySelector(SEL.injectDistress),
      'distress',
      'Distress',
      heavyDistresses,
      state.distressHandle,
      function (v) {
        state.distressHandle = v;
        updateAccordionSelected('Distress', selectedLabelFromHandle(heavyDistresses, v));
        refresh();
      },
      { rugModifier: 'color', showTierPremium: false }
    );

    renderMirrorSwatchGroup(
      document.querySelector(SEL.injectFrame),
      'frame',
      'Frame',
      data.frames || [],
      state.frameHandle,
      function (v) {
        state.frameHandle = v;
        updateAccordionSelected('Frame', selectedLabelFromHandle(data.frames, v));
        updateProductGalleryForSelection();
        refresh();
      },
      { rugModifier: 'frame', showTierPremium: true }
    );

    updateDimensionUI(state.shapeHandle === ROUND_HANDLE);
    updateAccordionSelected('Shape', selectedLabelFromHandle(data.shapes, state.shapeHandle));
    updateAccordionSelected('Color', selectedLabelFromHandle(data.colors, state.colorHandle));
    updateAccordionSelected('Distress', selectedLabelFromHandle(heavyDistresses, state.distressHandle));
    updateAccordionSelected('Frame', selectedLabelFromHandle(data.frames, state.frameHandle));
  }

  function boot() {
    var root = document.querySelector(SEL.root);
    if (!root || root.getAttribute('data-mirror-bootstrapped') === '1') return;
    root.setAttribute('data-mirror-bootstrapped', '1');

    if (typeof window.initializeCylindoToggles === 'function') {
      window.initializeCylindoToggles();
    }

    if (!getData()) {
      renderErrors(['Mirror configuration could not be loaded. Please refresh the page.']);
      renderPdpPrice(false, null);
      setAddToCartEnabled(false);
      return;
    }

    var atc = getAddToCartButton();
    if (atc && !atc.hasAttribute('data-mirror-atc-baseline')) {
      atc.setAttribute('data-mirror-atc-baseline', atc.disabled ? '1' : '0');
    }

    initSelectors();
    bindDimensionInputs();
    bindMirrorGalleryLifecycle();
    var wEl = document.querySelector(SEL.width);
    var hEl = document.querySelector(SEL.height);
    if (wEl) state.widthInput = wEl.value;
    if (hEl) state.heightInput = hEl.value;
    refresh();
    if (state.shapeHandle && state.colorHandle && state.frameHandle) {
      scheduleInitialMirrorGalleryImage();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);

  /** Title-case words for customer-facing labels (e.g. "round" → "Round"). */
  function toPresentableLabel(s) {
    if (!s) return '';
    return String(s)
      .trim()
      .split(/\s+/)
      .map(function (w) {
        if (!w) return '';
        return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
      })
      .join(' ');
  }

  /**
   * Line item properties for Cart Transform / checkout (add-to-cart-handler.js).
   * Returns null if configuration is incomplete or invalid.
   */
  function getMirrorCartLineProperties() {
    var data = getData();
    if (!data) return null;

    var validation = runValidation();
    if (!validation.valid) return null;

    var shape = findByHandle(data.shapes, state.shapeHandle);
    var color = findByHandle(data.colors, state.colorHandle);
    var frame = findByHandle(data.frames, state.frameHandle);
    var distress = findByHandle(data.distresses, state.distressHandle);
    if (!shape || !color || !frame || !distress) return null;

    var isRound = state.shapeHandle === ROUND_HANDLE;
    var config = normalizeConfig(data.config);
    if (!config) return null;

    var resolved = getResolvedDimensions(config, isRound);
    var wStr = resolved.widthStr;
    var hStr = resolved.heightStr;
    if (!wStr || (!isRound && !hStr)) return null;

    var shapeLower = String(state.shapeHandle || '')
      .toLowerCase()
      .trim();

    var colorTierNum = Number(color.tier);
    var colorTier = colorTierNum === 2 ? '2' : colorTierNum === 1.5 ? '15' : '1';
    var frameTier = Number(frame.tier) === 2 ? '2' : '1';

    var colorLabel = color.name || state.colorHandle || '';
    var frameLabel = frame.name || state.frameHandle || '';
    var distressLabel = distress.name || state.distressHandle || '';
    var shapeTitle = shape.name || state.shapeHandle || '';

    var comboImageUrl = getMirrorImage(state.shapeHandle, state.colorHandle, state.frameHandle)
      || getColorImageUrl(state.colorHandle)
      || '';

    return {
      _mirrorShape: shapeLower,
      _mirrorColor: toPresentableLabel(colorLabel),
      _mirrorFrame: toPresentableLabel(frameLabel),
      _mirrorDistress: toPresentableLabel(distressLabel),
      _mirrorColorTier: colorTier,
      _mirrorFrameTier: frameTier,
      _mirrorWidthIn: wStr,
      _mirrorHeightIn: hStr,
      _mirrorImage: comboImageUrl,
      _mirrorComboImageUrl: comboImageUrl,
      Shape: toPresentableLabel(shapeTitle),
      Color: toPresentableLabel(colorLabel),
      Frame: toPresentableLabel(frameLabel),
      Distress: toPresentableLabel(distressLabel),
      Width: wStr + '"',
      Height: hStr + '"',
    };
  }

  /**
   * Writes the mirror properties object into the hidden `properties[...]` inputs
   * inside `[data-mirror-calculator]`. When props is null (invalid state), all
   * inputs are cleared so stale values don't sneak into FormData.
   */
  function syncCartPropertyInputs(props) {
    var root = document.querySelector('[data-mirror-calculator]');
    if (!root) return;
    var inputs = root.querySelectorAll('[data-mirror-prop]');
    inputs.forEach(function (input) {
      var key = input.getAttribute('data-mirror-prop');
      input.value = props && props[key] != null ? props[key] : '';
    });
  }

  window.getMirrorCartLineProperties = getMirrorCartLineProperties;
})();
